import { AxiosError, AxiosResponse } from 'axios';
import qs from 'qs-stringify';

import { RESPONSE_CODES } from '../constants/responseCodes.constants';
import { PaginationFactory } from '../factory';
import { api } from '../http';
import { store } from '../store';
import { DefaultCallback, DefaultRes } from '../types';
import { PomBannerTypes } from '../types/pomBanner.types';

const bannerUri = 'banners';

export class PomBannerService {
    private static get adminDispatch() {
        return store.dispatch.bannersAdmin;
    }

    public static async fetchMany(
        params: { page: number; perPage: number },
        callback?: DefaultCallback,
    ): Promise<PaginationFactory<PomBannerTypes.Model> | void> {
        const { adminDispatch } = PomBannerService;

        try {
            adminDispatch.toggleLoading('fetchMany', true);

            const qsfy = qs({
                perPage: params.perPage,
                page: params.page,
            });

            const {
                data: {
                    result: { items, page, perPage, total },
                    isValid,
                    message,
                },
            } = await api.get<
                DefaultRes<PaginationFactory<PomBannerTypes.Model>>,
                AxiosResponse<DefaultRes<PaginationFactory<PomBannerTypes.Model>>>
            >(`/${bannerUri}?${qsfy}`);

            adminDispatch.toggleLoading('fetchMany', false);

            if (isValid) {
                adminDispatch.setPagination({ items, page, perPage, total });
                callback && callback(isValid, RESPONSE_CODES[message]);
                return { items, page, perPage, total };
            }

            callback && callback(false, RESPONSE_CODES[message]);
        } catch (error: any) {
            adminDispatch.toggleLoading('fetchMany', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<PomBannerTypes.Model>>
                ).response?.data?.message;
                return callback &&
                    callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
            }
            callback && callback(false, String(error));
        }
    }

    public static async create(
        payload: PomBannerTypes.CreateParams,
        callback: DefaultCallback,
    ) {
        const { adminDispatch } = PomBannerService;

        try {
            adminDispatch.toggleLoading('create', true);

            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<PomBannerTypes.Model>,
                AxiosResponse<DefaultRes<PomBannerTypes.Model>>
            >(`/${bannerUri}`, payload);

            adminDispatch.toggleLoading('create', false);

            if (isValid) {
                return callback(true, 'Banner created successfully');
            }

            return callback(false, RESPONSE_CODES[message]);
        } catch (error: any) {
            adminDispatch.toggleLoading('create', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<PomBannerTypes.Model>>
                ).response?.data?.message;
                return callback(
                    false,
                    RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN,
                );
            }
            return callback(false, String(error));
        }
    }

    public static async update(
        id: string,
        payload: PomBannerTypes.UpdateParams,
        callback: DefaultCallback,
    ) {
        const { adminDispatch } = PomBannerService;

        try {
            adminDispatch.toggleLoading('update', true);

            const {
                data: { result, isValid, message },
            } = await api.put<
                DefaultRes<PomBannerTypes.Model>,
                AxiosResponse<DefaultRes<PomBannerTypes.Model>>
            >(`/${bannerUri}/${id}`, payload);

            adminDispatch.toggleLoading('update', false);

            if (isValid) {
                return callback(true, 'Banner updated successfully');
            }

            return callback(false, RESPONSE_CODES[message]);
        } catch (error: any) {
            adminDispatch.toggleLoading('update', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<PomBannerTypes.Model>>
                ).response?.data?.message;
                return callback(
                    false,
                    RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN,
                );
            }
            return callback(false, String(error));
        }
    }

    public static async fetchActive(): Promise<PomBannerTypes.Model[]> {
        try {
            const {
                data: { result, isValid },
            } = await api.get<
                DefaultRes<PomBannerTypes.Model[]>,
                AxiosResponse<DefaultRes<PomBannerTypes.Model[]>>
            >(`/${bannerUri}/active`);

            return isValid ? result : [];
        } catch {
            return [];
        }
    }

    public static async dismiss(bannerId: string): Promise<boolean> {
        try {
            const {
                data: { isValid },
            } = await api.post<
                DefaultRes<boolean>,
                AxiosResponse<DefaultRes<boolean>>
            >(`/${bannerUri}/${bannerId}/dismiss`);

            return isValid;
        } catch {
            return false;
        }
    }

    public static async remove(id: string, callback: DefaultCallback) {
        const { adminDispatch } = PomBannerService;

        try {
            adminDispatch.toggleLoading('remove', true);

            const {
                data: { isValid, message },
            } = await api.delete<
                DefaultRes<boolean>,
                AxiosResponse<DefaultRes<boolean>>
            >(`/${bannerUri}/${id}`);

            adminDispatch.toggleLoading('remove', false);

            if (isValid) {
                adminDispatch.removeItem(id);
                return callback(true, 'Banner deleted successfully');
            }

            return callback(false, RESPONSE_CODES[message]);
        } catch (error: any) {
            adminDispatch.toggleLoading('remove', false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<boolean>>
                ).response?.data?.message;
                return callback(
                    false,
                    RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN,
                );
            }
            return callback(false, String(error));
        }
    }
}
